import { useTranslation } from '@govard/i18n-core/react';
import { 
  Plus, 
  LayoutDashboard, 
  Crown,
  Briefcase,
  Code2,
  Palette,
  Settings,
  ChevronDown,
  ChevronUp,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Row, Col, Card } from 'antd';



import {
  ActivePipelinesWidget,
  AgentStatusWidget,
  BurndownWidget,
  CalendarWidget,
  FocusTodayWidget,
  NotificationCenterWidget,
  QuickActionsWidget,
  RecentActivityWidget,
  TodaysStatsWidget,
} from '../components/dashboard/index.js';
import { useDashboardViewModel } from '../features/dashboard/useDashboardViewModel.js';



interface DashboardPreset {
  id: 'owner' | 'admin' | 'manager';
  labelKey: string;
  icon: LucideIcon;
}

interface WidgetDef {
  id: DashboardWidgetId;
  label: string;
  span: number;
}

type DashboardWidgetId =
  | 'stats'
  | 'focus'
  | 'workforce'
  | 'burndown'
  | 'pipelines'
  | 'activity'
  | 'calendar'
  | 'actions'
  | 'notifications';

const DASHBOARD_LAYOUT_STORAGE_KEY = 'govardos-dashboard-layout-v5';
const DEFAULT_PRESET: DashboardPreset['id'] = 'admin';

const DASHBOARD_PRESETS: readonly DashboardPreset[] = [
  { id: 'owner', labelKey: 'CEO', icon: Crown },
  { id: 'admin', labelKey: 'PM', icon: Briefcase },
  { id: 'manager', labelKey: 'Design', icon: Palette },
] as const;

const PRESET_WIDGETS: Readonly<Record<DashboardPreset['id'], readonly DashboardWidgetId[]>> = {
  owner: ['stats', 'focus', 'workforce'],
  admin: ['stats', 'focus', 'workforce', 'burndown', 'pipelines', 'activity', 'calendar', 'actions', 'notifications'],
  manager: ['focus', 'activity', 'notifications'],
} as const;

const WIDGET_LABELS: Readonly<Record<DashboardWidgetId, string>> = {
  stats: 'stats',
  focus: 'focus',
  workforce: 'workforce',
  burndown: 'burndown',
  pipelines: 'pipelines',
  activity: 'activity',
  calendar: 'calendar',
  actions: 'actions',
  notifications: 'notifications',
} as const;

const WIDGET_REGISTRY: readonly WidgetDef[] = [
  { id: 'stats', label: 'stats', span: 12 },
  { id: 'focus', label: 'focus', span: 8 },
  { id: 'workforce', label: 'workforce', span: 4 },
  { id: 'burndown', label: 'burndown', span: 8 },
  { id: 'pipelines', label: 'pipelines', span: 4 },
  { id: 'activity', label: 'activity', span: 8 },
  { id: 'calendar', label: 'calendar', span: 4 },
  { id: 'actions', label: 'actions', span: 4 },
  { id: 'notifications', label: 'notifications', span: 4 },
] as const;



function readStoredLayout(): DashboardWidgetId[] | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const raw = window.localStorage.getItem(DASHBOARD_LAYOUT_STORAGE_KEY);

  if (!raw) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as DashboardWidgetId[];

    if (!Array.isArray(parsed)) {
      return null;
    }

    return parsed.filter((widgetId): widgetId is DashboardWidgetId => widgetId in WIDGET_LABELS);
  } catch {
    return null;
  }
}

function persistLayout(layout: readonly DashboardWidgetId[]): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(DASHBOARD_LAYOUT_STORAGE_KEY, JSON.stringify(layout));
}

export default function DashboardPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { viewModel, dealsLoading, hotLeadsLoading } = useDashboardViewModel();
  const [activePreset, setActivePreset] = useState<DashboardPreset['id']>(DEFAULT_PRESET);
  const [customizing, setCustomizing] = useState(false);
  const [layout, setLayout] = useState<DashboardWidgetId[]>(() => readStoredLayout() ?? [...PRESET_WIDGETS[DEFAULT_PRESET]]);
  
  const applyPreset = (presetId: DashboardPreset['id']): void => {
    const nextLayout = [...PRESET_WIDGETS[presetId]];
    setActivePreset(presetId);
    setLayout(nextLayout);
    setCustomizing(false);
    persistLayout(nextLayout);
  };
  
  const removeWidget = (widgetId: DashboardWidgetId): void => {
    const nextLayout = layout.filter((currentWidgetId) => currentWidgetId !== widgetId);
    setLayout(nextLayout);
    persistLayout(nextLayout);
  };
  
  const addWidget = (widgetId: DashboardWidgetId): void => {
    if (layout.includes(widgetId)) {
      return;
    }

    const presetOrder = PRESET_WIDGETS[activePreset];
    const insertionIndex = presetOrder.indexOf(widgetId);

    if (insertionIndex === -1) {
      const nextLayout = [...layout, widgetId];
      setLayout(nextLayout);
      persistLayout(nextLayout);
      return;
    }

    const nextLayout = [...layout];
    nextLayout.splice(Math.min(insertionIndex, nextLayout.length), 0, widgetId);
    setLayout(nextLayout);
    persistLayout(nextLayout);
  };
  
  const moveWidget = (widgetId: DashboardWidgetId, direction: -1 | 1): void => {
    const index = layout.indexOf(widgetId);
    const targetIndex = index + direction;

    if (index === -1 || targetIndex < 0 || targetIndex >= layout.length) {
      return;
    }

    const nextLayout = [...layout];
    [nextLayout[index], nextLayout[targetIndex]] = [nextLayout[targetIndex]!, nextLayout[index]!] as [DashboardWidgetId, DashboardWidgetId];
    setLayout(nextLayout);
    persistLayout(nextLayout);
  };

  const renderWidgetActions = (widgetId: DashboardWidgetId): ReactElement | null => {
    if (!customizing) {
      return null;
    }

    const index = layout.indexOf(widgetId);

    return (
      <div className="flex justify-end bg-white" style={{ gap: 4, padding: '4px 8px', borderBottom: '1px solid #e7e5e4' }}>
        {index > 0 && (
          <Button
            size="small"
            type="text"
            icon={<ChevronUp size={14} />}
            aria-label={`Move ${WIDGET_LABELS[widgetId]} up`}
            onClick={() => moveWidget(widgetId, -1)}
          />
        )}
        {index < layout.length - 1 && (
          <Button
            size="small"
            type="text"
            icon={<ChevronDown size={14} />}
            aria-label={`Move ${WIDGET_LABELS[widgetId]} down`}
            onClick={() => moveWidget(widgetId, 1)}
          />
        )}
        <Button
          size="small"
          type="text"
          danger
          icon={<X size={14} />}
          aria-label={`Remove ${WIDGET_LABELS[widgetId]}`}
          onClick={() => removeWidget(widgetId)}
        />
      </div>
    );
  };

  const renderWidget = (widgetId: DashboardWidgetId): ReactElement | null => {
    switch (widgetId) {
      case 'stats':
        return <TodaysStatsWidget stats={viewModel.stats} />;
      case 'focus':
        return (
          <FocusTodayWidget
            items={viewModel.focusItems}
            loading={dealsLoading || hotLeadsLoading}
            testId="dashboard-widget-focus"
            onViewAll={() => navigate('/hot-leads')}
            onItemClick={(item) => navigate(`/deals/${item.id}`)}
          />
        );
      case 'workforce':
        return <AgentStatusWidget agents={viewModel.workforce} testId="dashboard-widget-workforce" />;
      case 'burndown':
        return <BurndownWidget data={viewModel.burndownData} loading={dealsLoading} testId="dashboard-widget-burndown" />;
      case 'pipelines':
        return (
          <ActivePipelinesWidget
            pipelines={viewModel.pipelines}
            loading={dealsLoading}
            testId="dashboard-widget-pipelines"
            onItemClick={() => navigate('/deals')}
          />
        );
      case 'activity':
        return (
          <RecentActivityWidget
            items={viewModel.activityItems}
            loading={dealsLoading}
            testId="dashboard-widget-activity"
            onItemClick={(item) => navigate(item.dealId ? `/deals/${item.dealId}` : '/deals')}
          />
        );
      case 'calendar':
        return (
          <CalendarWidget
            deadlines={viewModel.deadlines}
            loading={dealsLoading}
            testId="dashboard-widget-calendar"
            onItemClick={(deadline) => navigate(deadline.id ? `/deals/${deadline.id}` : '/deals')}
          />
        );
      case 'actions':
        return (
          <QuickActionsWidget
            testId="dashboard-widget-actions"
            actions={[
              {
                id: 'new-project',
                label: t('crm.dashboard.actions.new_project.label'),
                subtitle: t('crm.dashboard.actions.new_project.subtitle'),
                color: '#c47322',
                background: '#fffbeb',
                icon: Briefcase,
                onClick: () => navigate('/deals'),
              },
              {
                id: 'summon-agent',
                label: t('crm.dashboard.actions.summon_agent.label'),
                subtitle: t('crm.dashboard.actions.summon_agent.subtitle'),
                color: '#2563eb',
                background: '#eff6ff',
                icon: Code2,
                onClick: () => navigate('/hot-leads'),
              },
              {
                id: 'new-pipeline',
                label: t('crm.dashboard.actions.new_pipeline.label'),
                subtitle: t('crm.dashboard.actions.new_pipeline.subtitle'),
                color: '#16a34a',
                background: '#f0fdf4',
                icon: LayoutDashboard,
                onClick: () => navigate('/deals'),
              },
              {
                id: 'analytics',
                label: t('crm.dashboard.actions.analytics.label'),
                subtitle: t('crm.dashboard.actions.analytics.subtitle'),
                color: '#d97706',
                background: '#fffbeb',
                icon: Palette,
                onClick: () => navigate('/dashboard'),
              },
              {
                id: 'settings',
                label: t('crm.dashboard.actions.settings.label'),
                subtitle: t('crm.dashboard.actions.settings.subtitle'),
                color: '#78716c',
                background: '#f5f5f4',
                icon: Crown,
                onClick: () => navigate('/settings'),
              },
            ]}
          />
        );
      case 'notifications':
        return (
          <NotificationCenterWidget
            notifications={viewModel.notifications}
            loading={hotLeadsLoading}
            testId="dashboard-widget-notifications"
            onMarkAllRead={async () => undefined}
            onItemClick={() => navigate('/deals')}
          />
        );
      default:
        return null;
    }
  };

  const rows: { widgets: { id: DashboardWidgetId; def: WidgetDef }[] }[] = [];
  let currentRow: { id: DashboardWidgetId; def: WidgetDef }[] = [];
  let rowSpan = 0;

  layout.forEach((widgetId) => {
    const def = WIDGET_REGISTRY.find((item) => item.id === widgetId);
    if (!def) return;

    if (def.span >= 12) {
      if (currentRow.length > 0) {
        rows.push({ widgets: currentRow });
        currentRow = [];
        rowSpan = 0;
      }
      rows.push({ widgets: [{ id: widgetId, def }] });
      return;
    }

    currentRow.push({ id: widgetId, def });
    rowSpan += def.span;

    if (rowSpan >= 12) {
      rows.push({ widgets: currentRow });
      currentRow = [];
      rowSpan = 0;
    }
  });

  if (currentRow.length > 0) {
    rows.push({ widgets: currentRow });
  }

  const availableWidgets = WIDGET_REGISTRY.filter((widget) => !layout.includes(widget.id));

  return (
    <div 
      className="animate-fade-in flex flex-col h-full" 
      data-testid="dashboard-mission-control"
    >
      <div className="flex flex-col flex-1 w-full max-w-[1600px] mx-auto overflow-hidden">
        {/* Header Area */}
        <header className="flex justify-between items-start shrink-0 z-10 w-full pb-8">
        <div>
          <h1 className="font-bold text-[#2d2418] m-0 mb-1 leading-tight tracking-tight" style={{ fontSize: 28 }}>
            Mission Control
          </h1>
          <p className="m-0" style={{ color: '#a89b8a', fontSize: 14 }}>
            Welcome back. Here is what is happening across your systems.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-full" style={{ padding: 4, background: '#f5f5f4', gap: 4 }}>
            {DASHBOARD_PRESETS.map((preset) => (
              <button
                key={preset.id}
                type="button"
                aria-pressed={activePreset === preset.id}
                onClick={() => applyPreset(preset.id)}
                className="flex items-center gap-1.5 rounded-full border-0 px-3 py-1 text-sm font-medium"
                style={{
                  backgroundColor: activePreset === preset.id ? '#d97706' : 'transparent',
                  color: activePreset === preset.id ? '#fff' : '#44403c',
                  cursor: 'pointer',
                }}
              >
                <preset.icon size={14} />
                {preset.labelKey}
              </button>
            ))}
          </div>
          {customizing ? (
            <Button onClick={() => applyPreset(activePreset)}>
              Reset
            </Button>
          ) : null}
          <Button 
            className="ml-2 rounded-full"
            icon={<Settings size={14} />}
            onClick={() => setCustomizing((v) => !v)}
          >
            {customizing ? 'Done' : 'Customize'}
          </Button>
        </div>
        </header>

        {/* Widgets Grid */}
        <main className="flex-1 overflow-y-auto flex flex-col gap-8 w-full pr-2">
          {rows.map((row, rowIndex) => (
            <Row key={`dashboard-row-${rowIndex}`} gutter={[24, 24]}>
              {row.widgets.map(({ id: widgetId, def }) => {
                const content = renderWidget(widgetId);
                if (!content) return null;

                const testId = widgetId === 'focus'
                  ? 'dashboard-focus-list'
                  : widgetId === 'workforce'
                    ? 'dashboard-workforce-summary'
                    : widgetId === 'pipelines'
                      ? 'dashboard-pipelines'
                      : undefined;

                return (
                  <Col key={widgetId} span={24} xl={def.span * 2} data-testid={testId}>
                    {renderWidgetActions(widgetId)}
                    {content}
                  </Col>
                );
              })}
            </Row>
          ))}
        </main>
      </div>

      {/* Widget Picker */}
      {customizing && availableWidgets.length > 0 && (
        <Card size="small" title={<span className="uppercase font-semibold tracking-wide" style={{ color: '#78716c', fontSize: 11 }}>Add widgets</span>} bordered={false} style={{ borderRadius: 0, borderTop: '1px solid #e7e5e4' }}>
          <Space wrap>
            {availableWidgets.map((widget) => (
              <Button
                key={widget.id}
                size="small"
                aria-label={`Add ${widget.label}`}
                icon={<Plus size={14} />}
                onClick={() => addWidget(widget.id)}
              >
                {widget.label}
              </Button>
            ))}
          </Space>
        </Card>
      )}
    </div>
  );
}
